import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trophy, Store, Map, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSound } from '../hooks/useSound';
import { MiniKit } from '@worldcoin/minikit-js';
import { sendLightHapticFeedback } from '../utils/haptics';

const FOOTER_BASE_H = 76;   // 黑色背景「可視內容高度」，不含 safe area
const CONTENT_LIFT_PX = 18; // 真正抬高整排（改呢個一定見到郁）
const SIDE_SAFE_PX = 10;    // 左右內縮（避開 iPhone 圓角裁切）

const LABEL_LINE_H = 12;
const LABEL_LINES = 2;
const LABEL_H = LABEL_LINE_H * LABEL_LINES; // 24px
const ICON_H = 24;

interface MobileFooterProps {
  onRequireLogin: () => void;
}

const MobileFooter: React.FC<MobileFooterProps> = ({ onRequireLogin }) => {
  const [isVibrating, setIsVibrating] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();

  const { play: playFooterSfx } = useSound(['/mp3/gg_1.mp3', '/mp3/gg_2.mp3'], {
    timeoutMs: 5000,
    bustCache: true
  });

  const handleLogoClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!user) {
      event.preventDefault();
      onRequireLogin();
    }

    try {
      if (MiniKit.isInstalled()) {
        MiniKit.sendHapticFeedback({ hapticsType: 'impact', style: 'light' });
      } else if ('vibrate' in navigator && navigator.vibrate) {
        navigator.vibrate(50);
      }
    } catch {
      if ('vibrate' in navigator && navigator.vibrate) {
        try {
          navigator.vibrate(50);
        } catch {
          // Native vibration is optional.
        }
      }
    }

    try {
      await playFooterSfx();
    } catch {
      // Footer feedback is best-effort and must not block navigation.
    }

    setIsVibrating(true);
    setTimeout(() => setIsVibrating(false), 300);
  };

  const handleProtectedLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    void sendLightHapticFeedback();
    if (!user) {
      event.preventDefault();
      onRequireLogin();
    }
  };

  const isActivePage = (path: string) => location.pathname === path;

  const itemBase = 'min-w-0 flex flex-col items-center justify-end h-full px-0.5 gap-[3px]';
  const activeText = 'text-white';
  const idleText = 'text-gray-400';

  return (
    <footer
      className="fixed bottom-0 left-0 right-0 z-40 bg-black sm:hidden box-border relative"
      style={{
        height: `calc(${FOOTER_BASE_H}px + env(safe-area-inset-bottom))`,
      }}
    >
      {/* 內容層：固定貼住 safe-area 上面，然後用 transform 真正抬高 */}
      <div
        className="absolute left-0 right-0"
        style={{
          bottom: 'env(safe-area-inset-bottom)',
          height: `${FOOTER_BASE_H}px`,
          paddingLeft: `${SIDE_SAFE_PX}px`,
          paddingRight: `${SIDE_SAFE_PX}px`,
          transform: `translateY(-${CONTENT_LIFT_PX}px)`,
          willChange: 'transform',
        }}
      >
        <div className="h-full grid grid-cols-5 items-end">
          {/* Power Leaderboard */}
          <Link to="/leaderboard" onClick={handleProtectedLinkClick} className={itemBase}>
            <div style={{ height: ICON_H }} className="flex items-center justify-center">
              <Trophy className={`w-6 h-6 ${isActivePage('/leaderboard') ? activeText : idleText}`} />
            </div>
            <span
              className={`footer-label-2line text-[11px] font-semibold text-center px-0.5 ${
                isActivePage('/leaderboard') ? activeText : idleText
              }`}
              style={{ height: `${LABEL_H}px`, lineHeight: `${LABEL_LINE_H}px` }}
            >
              {t('footer.powerLeaderboard')}
            </span>
          </Link>

            {/* Throne Battle */}
          <Link to="/throne-battle" onClick={handleProtectedLinkClick} className={itemBase}>
            <div style={{ height: ICON_H }} className="flex items-center justify-center">
              <Crown className={`w-6 h-6 ${isActivePage('/throne-battle') ? activeText : idleText}`} />
            </div>
            <span
              className={`footer-label-2line text-[11px] font-semibold text-center px-0.5 ${
                isActivePage('/throne-battle') ? activeText : idleText
              }`}
              style={{ height: `${LABEL_H}px`, lineHeight: `${LABEL_LINE_H}px` }}
            >
              {t('footer.throneBattle')}
            </span>
          </Link>

          {/* Center Logo */}
          <Link to="/game" onClick={handleLogoClick} className="min-w-0 flex items-end justify-center h-full px-0.5">
            <img
              src="/image/logo/bbg_icon_01.png"
              alt="GGmomomo Logo"
              className={`h-[56px] w-auto ${isVibrating ? 'animate-vibrate-once' : ''}`}
            />
          </Link>

          {/* Mining */}
          <Link to="/mining-map" onClick={handleProtectedLinkClick} className={itemBase}>
            <div style={{ height: ICON_H }} className="flex items-center justify-center">
              <Map className={`w-6 h-6 ${isActivePage('/mining-map') ? activeText : idleText}`} />
            </div>
            <span
              className={`footer-label-2line text-[11px] font-semibold text-center px-0.5 ${
                isActivePage('/mining-map') ? activeText : idleText
              }`}
              style={{ height: `${LABEL_H}px`, lineHeight: `${LABEL_LINE_H}px` }}
            >
              {t('footer.mining')}
            </span>
          </Link>

          {/* Shop */}
          <Link to="/shop" onClick={handleProtectedLinkClick} className={itemBase}>
            <div style={{ height: ICON_H }} className="flex items-center justify-center">
              <Store className={`w-6 h-6 ${isActivePage('/shop') ? activeText : idleText}`} />
            </div>
            <span
              className={`footer-label-2line text-[11px] font-semibold text-center px-0.5 ${
                isActivePage('/shop') ? activeText : idleText
              }`}
              style={{ height: `${LABEL_H}px`, lineHeight: `${LABEL_LINE_H}px` }}
            >
              {t('footer.bubbleShop')}
            </span>
          </Link>
        </div>
      </div>

      <style>
        {`
          .footer-label-2line {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }

          @media (max-width: 360px) {
            .footer-label-2line { display: none; }
          }
        `}
      </style>
    </footer>
  );
};

export default MobileFooter;
